import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Card,
  Row as BootstrapRow,
  Col as BootstrapCol,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Table,
  Nav,
} from "react-bootstrap";
import { AutoComplete, Select } from "antd";
import { FilterOutlined, PlusOutlined } from "@ant-design/icons";
import FormItem from "@/common/FormItem/FormItem";
import { Link } from "react-router-dom";
import PageTitle from "@/common/PageTitle/PageTitle";
import toast from "react-hot-toast";
import styles from "./brands.module.scss";
import {
  useArchiveBrandMutation,
  useCreateBrandMutation,
  useGetBrandLogoUploadUrlMutation,
  useLazyGetBrandRestoreEligibilityQuery,
  useListBrandsQuery,
  usePermanentDeleteBrandMutation,
  useRestoreBrandMutation,
  useUpdateBrandMutation,
  type BrandArchivePreflight,
  type BrandRow,
} from "@/features/brands/brandsApi";
import {
  industrySelectOptions,
  mergeIndustryOptions,
} from "@/features/brands/brandIndustries";
import { normalizeBrandLogoFile } from "@/features/brands/normalizeBrandLogo";
import {
  BRAND_LIFECYCLE_MIN_REASON_LENGTH,
  BRAND_PERMANENT_DELETE_CONFIRM_TOKEN,
  archivePreflightFromError,
  brandAccessLevelLabel,
  canSubmitBrandArchive,
  canSubmitBrandPermanentDelete,
  defaultSelectedMembershipIds,
  extractApiErrorMessage,
  formatArchiveClientLabel,
  formatRestoreMembershipLabel,
  membershipIdsForRestore,
  preflightFromError,
  toggleMembershipSelection,
  type BrandLifecycleView,
  type BrandPermanentDeletePreflight,
} from "@/features/brands/brandLifecycle";
import { SONG_LIBRARY_ACTION_ICONS } from "@/styles/songLibraryActionIcons";
import { useCanPerformLifecycleDelete } from "@/contexts/SuperAdminIdentityContext";
import { formatTabCount } from "@/features/catalog-lifecycle/CatalogLifecycleTabs";
import BrandScreensaverFields from "@/features/screensavers/BrandScreensaverFields";
import {
  planBrandScreensaverSave,
  saveBrandThenScreensaver,
  uploadScreensaverLibraryFile,
  type ScreensaverChoice,
} from "@/features/screensavers/screensaverAssignmentModel";
import {
  useClearBrandScreensaverAssignmentMutation,
  useCreateScreensaverMutation,
  useGetBrandScreensaverAssignmentQuery,
  useSetBrandScreensaverAssignmentMutation,
  useSignScreensaverUploadMutation,
} from "@/features/screensavers/screensaversApi";

const emptyForm = {
  name: "",
  industry: "",
  description: "",
  status: "active",
};

const LOGO_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

const Brands: React.FC = () => {
  const canLifecycle = useCanPerformLifecycleDelete();
  const [view, setView] = useState<BrandLifecycleView>("active");
  const [filters, setFilters] = useState({
    brandName: undefined as string | undefined,
    industry: undefined as string | undefined,
    status: undefined as string | undefined,
  });
  const [resetCounter, setResetCounter] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [screensaverMode, setScreensaverMode] = useState<ScreensaverChoice>("global");
  const [screensaverFile, setScreensaverFile] = useState<File | null>(null);
  const [screensaverError, setScreensaverError] = useState<string | null>(null);
  const [createdBrandId, setCreatedBrandId] = useState<string | null>(null);
  const [editingBrand, setEditingBrand] = useState<BrandRow | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  /** Industries typed in this session that are not yet on a saved brand. */
  const [sessionIndustries, setSessionIndustries] = useState<string[]>([]);
  /** Live search text in Add/Edit Industry Select (allows custom industries). */
  const [industrySearch, setIndustrySearch] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoDragOver, setLogoDragOver] = useState(false);
  const [savingLogo, setSavingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Lifecycle modals
  const [archivingBrand, setArchivingBrand] = useState<BrandRow | null>(null);
  const [archiveNameConfirmation, setArchiveNameConfirmation] = useState("");
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [archivePreflight, setArchivePreflight] =
    useState<BrandArchivePreflight | null>(null);
  const [archivePreflightLoading, setArchivePreflightLoading] = useState(false);
  const [showAffectedClients, setShowAffectedClients] = useState(false);

  const [restoringBrand, setRestoringBrand] = useState<BrandRow | null>(null);
  const [selectedMembershipIds, setSelectedMembershipIds] = useState<string[]>(
    [],
  );
  const [restoreNotes, setRestoreNotes] = useState("");
  const [restoreConfirmed, setRestoreConfirmed] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const [pdBrand, setPdBrand] = useState<BrandRow | null>(null);
  const [pdPreflight, setPdPreflight] =
    useState<BrandPermanentDeletePreflight | null>(null);
  const [pdPhase, setPdPhase] = useState<
    "idle" | "preflight" | "confirm" | "running" | "error"
  >("idle");
  const [pdNameConfirmation, setPdNameConfirmation] = useState("");
  const [pdConfirmTyped, setPdConfirmTyped] = useState("");
  const [pdReason, setPdReason] = useState("");
  const [pdError, setPdError] = useState<string | null>(null);

  const activeList = useListBrandsQuery({ view: "active" });
  const archivedList = useListBrandsQuery({ view: "archived" });
  const listQuery = view === "archived" ? archivedList : activeList;

  const [createBrand, { isLoading: creating }] = useCreateBrandMutation();
  const [updateBrand, { isLoading: updating }] = useUpdateBrandMutation();
  const [getLogoUploadUrl] = useGetBrandLogoUploadUrlMutation();
  const [signScreensaver] = useSignScreensaverUploadMutation();
  const [createScreensaver] = useCreateScreensaverMutation();
  const [setBrandScreensaver] = useSetBrandScreensaverAssignmentMutation();
  const [clearBrandScreensaver] = useClearBrandScreensaverAssignmentMutation();
  const brandAssignmentQuery = useGetBrandScreensaverAssignmentQuery(
    editingBrand?.brandId || "",
    { skip: !editingBrand?.brandId },
  );
  const [archiveBrand, { isLoading: archiving }] = useArchiveBrandMutation();
  const [restoreBrand, { isLoading: restoring }] = useRestoreBrandMutation();
  const [permanentDeleteBrand, { isLoading: deleting }] =
    usePermanentDeleteBrandMutation();
  const [
    fetchRestoreEligibility,
    {
      data: restoreEligibility,
      isFetching: restoreEligibilityLoading,
      isError: restoreEligibilityError,
      error: restoreEligibilityErr,
    },
  ] = useLazyGetBrandRestoreEligibilityQuery();

  const { data, isLoading, isError, error, refetch } = listQuery;

  const brands = useMemo(() => {
    const rows = data?.brands || [];
    return rows.filter((b) => {
      const matchesName =
        !filters.brandName || b.name === filters.brandName;
      const matchesIndustry =
        !filters.industry || b.industry === filters.industry;
      const matchesStatus =
        !filters.status ||
        (filters.status === "Active" && b.status === "active") ||
        (filters.status === "Inactive" && b.status !== "active");
      return matchesName && matchesIndustry && matchesStatus;
    });
  }, [data, filters]);

  const industryOptions = useMemo(() => {
    const rows = [
      ...(activeList.data?.brands || []),
      ...(archivedList.data?.brands || []),
    ];
    return mergeIndustryOptions(
      rows.map((b) => b.industry),
      sessionIndustries,
    );
  }, [activeList.data, archivedList.data, sessionIndustries]);

  const modalIndustryOptions = useMemo(
    () =>
      mergeIndustryOptions(industryOptions, [form.industry, industrySearch]),
    [industryOptions, form.industry, industrySearch],
  );

  const filterOptions = useMemo(() => {
    const rows = data?.brands || [];
    return {
      brandNames: rows.map((b) => b.name),
      industries: industryOptions,
      statuses: ["Active", "Inactive"],
    };
  }, [data, industryOptions]);

  const rememberIndustry = (raw: string) => {
    const value = raw.trim();
    if (!value) return;
    setSessionIndustries((prev) =>
      prev.includes(value) ? prev : [...prev, value],
    );
  };

  const clearLogoSelection = () => {
    setLogoFile(null);
    setLogoPreviewUrl(null);
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const resetScreensaverDraft = () => {
    setScreensaverMode("global");
    setScreensaverFile(null);
    setScreensaverError(null);
    setCreatedBrandId(null);
  };

  const openCreate = () => {
    setFormError(null);
    setForm(emptyForm);
    setIndustrySearch("");
    setEditingBrand(null);
    clearLogoSelection();
    resetScreensaverDraft();
    setShowCreate(true);
  };

  const openEdit = (brand: BrandRow) => {
    setFormError(null);
    setEditingBrand(brand);
    setIndustrySearch("");
    clearLogoSelection();
    setForm({
      name: brand.name || "",
      industry: brand.industry || "",
      description: brand.description || "",
      status: brand.status === "active" ? "active" : "Inactive",
    });
    setShowCreate(false);
    resetScreensaverDraft();
  };

  const closeModals = () => {
    setShowCreate(false);
    setEditingBrand(null);
    setFormError(null);
    setForm(emptyForm);
    setIndustrySearch("");
    clearLogoSelection();
    resetScreensaverDraft();
  };

  const closeArchiveModal = () => {
    setArchivingBrand(null);
    setArchiveNameConfirmation("");
    setArchiveReason("");
    setArchiveError(null);
    setArchivePreflight(null);
    setArchivePreflightLoading(false);
    setShowAffectedClients(false);
  };

  const openArchiveModal = async (brand: BrandRow) => {
    if (!canLifecycle) return;
    setArchivingBrand(brand);
    setArchiveNameConfirmation("");
    setArchiveReason("");
    setArchiveError(null);
    setArchivePreflight(null);
    setShowAffectedClients(false);
    setArchivePreflightLoading(true);
    try {
      const result = await archiveBrand({
        brandId: brand.brandId,
        dryRun: true,
      }).unwrap();
      if ("preflight" in result && result.preflight) {
        setArchivePreflight(result.preflight);
        if (!result.preflight.eligible) {
          setArchiveError(
            result.preflight.blockers?.[0]?.message ||
              "Archive impact could not be determined; Archive is blocked.",
          );
        }
      } else {
        setArchiveError("Archive preflight response missing impact summary");
      }
    } catch (err) {
      const fromConflict = archivePreflightFromError(err);
      if (fromConflict) {
        setArchivePreflight(fromConflict);
        setArchiveError(
          fromConflict.eligible
            ? null
            : extractApiErrorMessage(
                err,
                "Archive impact could not be determined; Archive is blocked.",
              ),
        );
      } else {
        setArchiveError(
          extractApiErrorMessage(
            err,
            "Could not load authoritative Archive impact. Archive is blocked.",
          ),
        );
      }
    } finally {
      setArchivePreflightLoading(false);
    }
  };

  const closeRestoreModal = () => {
    setRestoringBrand(null);
    setSelectedMembershipIds([]);
    setRestoreNotes("");
    setRestoreConfirmed(false);
    setRestoreError(null);
  };

  const openRestoreModal = async (brand: BrandRow) => {
    if (!canLifecycle) return;
    setRestoringBrand(brand);
    setSelectedMembershipIds([]);
    setRestoreNotes("");
    setRestoreConfirmed(false);
    setRestoreError(null);
    try {
      const result = await fetchRestoreEligibility(brand.brandId).unwrap();
      setSelectedMembershipIds(
        defaultSelectedMembershipIds(result.eligibleMemberships || []),
      );
    } catch (err) {
      setRestoreError(
        extractApiErrorMessage(err, "Failed to load eligible memberships"),
      );
    }
  };

  const closePdModal = () => {
    setPdBrand(null);
    setPdPreflight(null);
    setPdPhase("idle");
    setPdNameConfirmation("");
    setPdConfirmTyped("");
    setPdReason("");
    setPdError(null);
  };

  const openPdModal = async (brand: BrandRow) => {
    if (!canLifecycle) return;
    setPdBrand(brand);
    setPdPreflight(null);
    setPdNameConfirmation("");
    setPdConfirmTyped("");
    setPdReason("");
    setPdError(null);
    setPdPhase("preflight");
    try {
      // dryRun skips typed-confirm validation; always returns preflight when archived.
      const result = await permanentDeleteBrand({
        brandId: brand.brandId,
        nameConfirmation: "",
        confirm: BRAND_PERMANENT_DELETE_CONFIRM_TOKEN,
        reason: "",
        dryRun: true,
      }).unwrap();
      if ("preflight" in result && result.preflight) {
        setPdPreflight(result.preflight);
        setPdPhase("confirm");
      } else {
        setPdError("Preflight response missing eligibility summary");
        setPdPhase("error");
      }
    } catch (err) {
      const fromConflict = preflightFromError(err);
      if (fromConflict) {
        setPdPreflight(fromConflict);
        setPdPhase("confirm");
        setPdError(
          fromConflict.eligible
            ? null
            : extractApiErrorMessage(err, "Permanent delete is blocked"),
        );
      } else {
        setPdError(
          extractApiErrorMessage(err, "Could not load deletion preflight"),
        );
        setPdPhase("error");
      }
    }
  };

  useEffect(() => {
    if (!logoPreviewUrl) return;
    return () => {
      URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  const applyLogoFile = async (file: File | null | undefined) => {
    if (!file) {
      clearLogoSelection();
      return;
    }
    if (!LOGO_ACCEPT.split(",").includes(file.type)) {
      setFormError("Logo must be JPEG, PNG, WebP, or GIF");
      clearLogoSelection();
      return;
    }
    setFormError(null);
    try {
      const normalized = await normalizeBrandLogoFile(file);
      setLogoFile(normalized);
      setLogoPreviewUrl(URL.createObjectURL(normalized));
    } catch (err: any) {
      setFormError(err?.message || "Could not process logo");
      clearLogoSelection();
    }
  };

  const onLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    void applyLogoFile(e.target.files?.[0]);
  };

  const onLogoDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLogoDragOver(true);
  };

  const onLogoDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLogoDragOver(false);
  };

  const onLogoDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLogoDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) {
      setFormError("Drop an image file (JPEG, PNG, WebP, or GIF)");
      return;
    }
    void applyLogoFile(file);
  };

  useEffect(() => {
    if (!editingBrand) return;
    const source = brandAssignmentQuery.data?.source;
    if (source === "brand") setScreensaverMode("custom");
    else if (source === "global") setScreensaverMode("global");
  }, [editingBrand, brandAssignmentQuery.data?.source]);

  const uploadAndAttachLogo = async (brandId: string, file: File) => {
    const { uploadUrl, key } = await getLogoUploadUrl({
      brandId,
      contentType: file.type,
    }).unwrap();

    const putRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!putRes.ok) {
      throw new Error(`Logo upload failed (HTTP ${putRes.status})`);
    }

    await updateBrand({ brandId, logoKey: key }).unwrap();
  };

  const applyScreensaverForBrand = async (brandId: string) => {
    const plan = planBrandScreensaverSave({
      mode: screensaverMode,
      hasNewFile: Boolean(screensaverFile),
      currentSource: brandAssignmentQuery.data?.source || null,
    });
    if (plan === "none") return;
    if (plan === "clear-override") {
      await clearBrandScreensaver(brandId).unwrap();
      return;
    }
    if (!screensaverFile) return;
    const uploaded = await uploadScreensaverLibraryFile({
      file: screensaverFile,
      sign: async (filename) => {
        const signed = await signScreensaver({ filename }).unwrap();
        return { url: signed.url };
      },
      put: async (url, file) => {
        const putRes = await fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "video/mp4" },
          body: file,
        });
        if (!putRes.ok) {
          throw new Error(`Screensaver upload failed (HTTP ${putRes.status})`);
        }
      },
      create: async (filename) => {
        const created = await createScreensaver({
          filename,
          name: filename.replace(/\.mp4$/i, ""),
          displayProfileId: "rk3568_player",
        }).unwrap();
        return { releaseId: created.screensaver.id };
      },
    });
    await setBrandScreensaver({
      brandId,
      releaseId: uploaded.releaseId,
    }).unwrap();
  };

  const handleCreate = async () => {
    setFormError(null);
    setScreensaverError(null);
    const industry = form.industry.trim();
    let nextBrandId = createdBrandId || "";
    const result = await saveBrandThenScreensaver({
      saveBrand: async () => {
        if (nextBrandId) return;
        const { brand } = await createBrand({
          name: form.name.trim(),
          industry: industry || undefined,
          description: form.description.trim() || undefined,
          status: form.status === "Inactive" ? "suspended" : "active",
        }).unwrap();
        if (!brand?.brandId) throw new Error("Create failed");
        nextBrandId = brand.brandId;
        setCreatedBrandId(brand.brandId);
        if (industry) rememberIndustry(industry);
        if (logoFile) {
          setSavingLogo(true);
          await uploadAndAttachLogo(brand.brandId, logoFile);
        }
      },
      applyScreensaver: () => applyScreensaverForBrand(nextBrandId),
    });
    setSavingLogo(false);
    if (!result.brandSaved) {
      setFormError(result.screensaverError || "Create failed");
      return;
    }
    if (result.screensaverError) {
      setScreensaverError(result.screensaverError);
      return;
    }
    closeModals();
  };

  const handleUpdate = async () => {
    if (!editingBrand) return;
    setFormError(null);
    setScreensaverError(null);
    const industry = form.industry.trim();
    const brandId = editingBrand.brandId;
    const result = await saveBrandThenScreensaver({
      saveBrand: async () => {
        await updateBrand({
          brandId,
          name: form.name.trim(),
          industry,
          description: form.description.trim(),
          status: form.status === "Inactive" ? "suspended" : "active",
        }).unwrap();
        if (industry) rememberIndustry(industry);
        if (logoFile) {
          setSavingLogo(true);
          await uploadAndAttachLogo(brandId, logoFile);
        }
      },
      applyScreensaver: () => applyScreensaverForBrand(brandId),
    });
    setSavingLogo(false);
    if (!result.brandSaved) {
      setFormError(result.screensaverError || "Update failed");
      return;
    }
    if (result.screensaverError) {
      setScreensaverError(result.screensaverError);
      return;
    }
    closeModals();
  };

  const handleArchive = async () => {
    if (!archivingBrand) return;
    if (
      !canSubmitBrandArchive({
        brandName: archivingBrand.name,
        nameConfirmation: archiveNameConfirmation,
        reason: archiveReason,
      })
    ) {
      setArchiveError(
        `Type the exact Brand name and a reason (min ${BRAND_LIFECYCLE_MIN_REASON_LENGTH} characters).`,
      );
      return;
    }
    setArchiveError(null);
    try {
      await archiveBrand({
        brandId: archivingBrand.brandId,
        nameConfirmation: archiveNameConfirmation,
        reason: archiveReason.trim(),
      }).unwrap();
      toast.success(`"${archivingBrand.name}" archived`);
      closeArchiveModal();
      setView("archived");
    } catch (err) {
      setArchiveError(extractApiErrorMessage(err, "Archive failed"));
    }
  };

  const handleRestore = async () => {
    if (!restoringBrand || !restoreConfirmed) return;
    setRestoreError(null);
    try {
      const membershipIds = membershipIdsForRestore(selectedMembershipIds);
      await restoreBrand({
        brandId: restoringBrand.brandId,
        membershipIds,
        notes: restoreNotes.trim() || undefined,
      }).unwrap();
      toast.success(
        `"${restoringBrand.name}" restored${
          membershipIds.length
            ? ` · ${membershipIds.length} membership(s) re-granted`
            : " · no memberships re-granted"
        }`,
      );
      closeRestoreModal();
      setView("active");
    } catch (err) {
      setRestoreError(extractApiErrorMessage(err, "Restore failed"));
    }
  };

  const handlePermanentDelete = async () => {
    if (!pdBrand || !pdPreflight?.eligible) return;
    if (
      !canSubmitBrandPermanentDelete({
        brandName: pdBrand.name,
        nameConfirmation: pdNameConfirmation,
        confirmTyped: pdConfirmTyped,
        reason: pdReason,
        eligible: pdPreflight.eligible,
      })
    ) {
      setPdError(
        `Type the exact Brand name, ${BRAND_PERMANENT_DELETE_CONFIRM_TOKEN}, and a reason (min ${BRAND_LIFECYCLE_MIN_REASON_LENGTH} characters).`,
      );
      return;
    }
    setPdError(null);
    setPdPhase("running");
    try {
      await permanentDeleteBrand({
        brandId: pdBrand.brandId,
        nameConfirmation: pdNameConfirmation,
        confirm: BRAND_PERMANENT_DELETE_CONFIRM_TOKEN,
        reason: pdReason.trim(),
      }).unwrap();
      toast.success(`"${pdBrand.name}" permanently deleted`);
      closePdModal();
    } catch (err) {
      const conflictPreflight = preflightFromError(err);
      if (conflictPreflight) {
        setPdPreflight(conflictPreflight);
      }
      setPdError(extractApiErrorMessage(err, "Permanent delete failed"));
      setPdPhase("error");
    }
  };

  const displayLogoSrc =
    logoPreviewUrl || editingBrand?.logoUrl || null;

  const logoField = (
    <Form.Group className="mb-3">
      <Form.Label>Brand logo</Form.Label>
      <div
        className={`${styles.logoDropZone}${logoDragOver ? ` ${styles.logoDropZoneActive}` : ""}`}
        onDragOver={onLogoDragOver}
        onDragEnter={onLogoDragOver}
        onDragLeave={onLogoDragLeave}
        onDrop={onLogoDrop}
      >
        <div className={styles.logoPicker}>
          <button
            type="button"
            className={styles.logoPreview}
            onClick={() => logoInputRef.current?.click()}
            aria-label="Upload brand logo"
          >
            {displayLogoSrc ? (
              <img src={displayLogoSrc} alt="" />
            ) : (
              <span className={styles.logoPlaceholder}>Drop logo</span>
            )}
          </button>
          <div className={styles.logoControls}>
            <Form.Control
              ref={logoInputRef}
              type="file"
              accept={LOGO_ACCEPT}
              onChange={onLogoFileChange}
            />
            <Form.Text muted>
              Recommended 512×512. JPEG, PNG, WebP, or GIF. Drag and drop
              onto the preview, or choose a file — resized and centered
              automatically.
            </Form.Text>
          </div>
        </div>
      </div>
    </Form.Group>
  );

  const industryField = (
    <Form.Group className="mb-3">
      <Form.Label>Industry</Form.Label>
      <Select
        className="customSelect"
        style={{ width: "100%" }}
        showSearch
        allowClear
        placeholder="Select or type an industry"
        value={form.industry || undefined}
        options={industrySelectOptions(modalIndustryOptions)}
        // Select opens the full list on click (unlike AutoComplete, which
        // filters by the current value — e.g. only "hospitality").
        getPopupContainer={() => document.body}
        dropdownStyle={{ zIndex: 9999 }}
        styles={{ popup: { root: { zIndex: 9999 } } }}
        filterOption={(input, option) =>
          String(option?.value ?? "")
            .toLowerCase()
            .includes(input.trim().toLowerCase())
        }
        onSearch={setIndustrySearch}
        onChange={(v) => {
          const next = v ? String(v) : "";
          setForm((f) => ({ ...f, industry: next }));
          setIndustrySearch("");
          if (next) rememberIndustry(next);
        }}
        onBlur={() => {
          const typed = industrySearch.trim();
          if (typed) {
            setForm((f) => ({ ...f, industry: typed }));
            rememberIndustry(typed);
            setIndustrySearch("");
          }
        }}
      />
      <Form.Text muted>
        Choose a suggested industry or type a new one to add it.
      </Form.Text>
    </Form.Group>
  );

  const busy = creating || updating || savingLogo;
  const archiveReady =
    !!archivingBrand &&
    !!archivePreflight &&
    archivePreflight.eligible === true &&
    !archivePreflightLoading &&
    canSubmitBrandArchive({
      brandName: archivingBrand.name,
      nameConfirmation: archiveNameConfirmation,
      reason: archiveReason,
    });
  const pdReady =
    pdBrand != null &&
    pdPreflight != null &&
    canSubmitBrandPermanentDelete({
      brandName: pdBrand.name,
      nameConfirmation: pdNameConfirmation,
      confirmTyped: pdConfirmTyped,
      reason: pdReason,
      eligible: pdPreflight.eligible,
    });

  const eligibleMemberships = restoreEligibility?.eligibleMemberships || [];

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <PageTitle title="Brands" />
            <Button
              onClick={() => {
                setFilters({
                  brandName: undefined,
                  industry: undefined,
                  status: undefined,
                });
                setResetCounter((c) => c + 1);
              }}
              style={{
                background: "white",
                color: "#333",
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                padding: "8px 16px",
                fontWeight: 500,
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>

          <p className="text-muted mb-3">
            Brands are platform entities. Assign a Brand to a Client Master
            Account to grant access to Locations and Zones under that Brand —
            Brands are never owned by a Master Account.
          </p>

          <Nav
            variant="tabs"
            activeKey={view}
            onSelect={(key) =>
              setView((key as BrandLifecycleView) || "active")
            }
            className="mb-3"
            data-testid="brands-lifecycle-tabs"
          >
            <Nav.Item>
              <Nav.Link eventKey="active">
                Active
                {formatTabCount(activeList.data?.brands?.length)}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="archived">
                Archived
                {formatTabCount(archivedList.data?.brands?.length)}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <div className={styles.form}>
            <BootstrapRow className="mb-2">
              <BootstrapCol md={4}>
                <FormItem label="Brand Name" layout="vertical">
                  <Select
                    key={`bn-${resetCounter}`}
                    showSearch
                    allowClear
                    className="customSelect"
                    placeholder="Any brand"
                    value={filters.brandName}
                    onChange={(v) =>
                      setFilters((f) => ({ ...f, brandName: v }))
                    }
                    options={filterOptions.brandNames.map((n) => ({
                      label: n,
                      value: n,
                    }))}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={4}>
                <FormItem label="Industry" layout="vertical">
                  <AutoComplete
                    key={`ind-${resetCounter}`}
                    allowClear
                    className="customSelect"
                    style={{ width: "100%" }}
                    placeholder="Any industry"
                    value={filters.industry}
                    options={industrySelectOptions(filterOptions.industries)}
                    filterOption={(input, option) =>
                      String(option?.value ?? "")
                        .toLowerCase()
                        .includes(input.trim().toLowerCase())
                    }
                    onChange={(v) => {
                      const next = v ? String(v) : undefined;
                      setFilters((f) => ({ ...f, industry: next }));
                      if (next) rememberIndustry(next);
                    }}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={4}>
                <FormItem label="Status" layout="vertical">
                  <Select
                    key={`st-${resetCounter}`}
                    allowClear
                    className="customSelect"
                    placeholder="Any status"
                    value={filters.status}
                    onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                    options={filterOptions.statuses.map((n) => ({
                      label: n,
                      value: n,
                    }))}
                  />
                </FormItem>
              </BootstrapCol>
            </BootstrapRow>
            {view === "active" ? (
              <BootstrapRow className="mb-3">
                <BootstrapCol className="d-flex justify-content-end">
                  <Button
                    style={{
                      background: "white",
                      color: "#e21e26",
                      border: "1px solid #e21e26",
                      borderRadius: "6px",
                      padding: "8px 16px",
                      fontWeight: 500,
                      height: 50,
                    }}
                    onClick={openCreate}
                  >
                    <PlusOutlined /> Add New Brand
                  </Button>
                </BootstrapCol>
              </BootstrapRow>
            ) : null}
          </div>

          {isLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          )}
          {isError && (
            <Alert variant="danger">
              {(error as any)?.data?.message || "Failed to load brands"}{" "}
              <Button size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </Alert>
          )}

          {!isLoading && !isError && (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Brand</th>
                  <th>Industry</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Locations</th>
                  <th>{view === "archived" ? "Archived" : "Created"}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {brands.map((b) => (
                  <tr key={b.brandId}>
                    <td>
                      <div className={styles.brandNameCell}>
                        {b.logoUrl ? (
                          <img
                            src={b.logoUrl}
                            alt=""
                            className={styles.brandThumb}
                          />
                        ) : (
                          <span className={styles.brandThumbPlaceholder} />
                        )}
                        <Link to={`/brands/${b.brandId}/locations`}>
                          {b.name}
                        </Link>
                      </div>
                    </td>
                    <td>{b.industry || "—"}</td>
                    <td>{b.description || "—"}</td>
                    <td>
                      {view === "archived" || b.archived || b.deletedAt
                        ? "Archived"
                        : b.status === "active"
                          ? "Active"
                          : b.status}
                    </td>
                    <td>{b.locationCount ?? 0}</td>
                    <td>
                      {view === "archived"
                        ? b.deletedAt?.slice(0, 10) || "—"
                        : b.createdAt?.slice(0, 10) || "—"}
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        {view === "active" ? (
                          <>
                            <Button
                              variant="link"
                              className={styles.actionLink}
                              onClick={() => openEdit(b)}
                              title="Edit brand"
                              aria-label={`Edit ${b.name}`}
                            >
                              <i className="bi bi-pencil" aria-hidden="true" />
                            </Button>
                            {canLifecycle ? (
                              <Button
                                variant="link"
                                className={styles.archiveLink}
                                onClick={() => openArchiveModal(b)}
                                title="Archive brand"
                                aria-label={`Archive ${b.name}`}
                              >
                                <i
                                  className={SONG_LIBRARY_ACTION_ICONS.archive}
                                  aria-hidden="true"
                                />
                              </Button>
                            ) : null}
                          </>
                        ) : canLifecycle ? (
                          <>
                            <Button
                              variant="link"
                              className={styles.actionLink}
                              onClick={() => void openRestoreModal(b)}
                              title="Restore brand"
                              aria-label={`Restore ${b.name}`}
                            >
                              <i
                                className={SONG_LIBRARY_ACTION_ICONS.restore}
                                aria-hidden="true"
                              />
                            </Button>
                            <Button
                              variant="link"
                              className={styles.archiveLink}
                              onClick={() => void openPdModal(b)}
                              title="Permanently delete brand"
                              aria-label={`Permanently delete ${b.name}`}
                            >
                              <i
                                className={
                                  SONG_LIBRARY_ACTION_ICONS.permanentDelete
                                }
                                aria-hidden="true"
                              />
                            </Button>
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {brands.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      {view === "archived"
                        ? "No archived brands."
                        : "No brands yet. Add a platform Brand, then assign it to Clients."}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showCreate} onHide={closeModals}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Brand</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Brand name *</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </Form.Group>
          {logoField}
          <BrandScreensaverFields
            mode={screensaverMode}
            onModeChange={setScreensaverMode}
            fileName={screensaverFile?.name || null}
            onFile={setScreensaverFile}
            existing={null}
            error={screensaverError}
          />
          {industryField}
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModals}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={busy || !form.name.trim()}
            onClick={handleCreate}
          >
            {busy ? "Creating…" : "Create Brand"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!editingBrand} onHide={closeModals}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Brand</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Brand name *</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </Form.Group>
          {logoField}
          <BrandScreensaverFields
            mode={screensaverMode}
            onModeChange={setScreensaverMode}
            fileName={screensaverFile?.name || null}
            onFile={setScreensaverFile}
            existing={
              brandAssignmentQuery.data
                ? {
                    source: brandAssignmentQuery.data.source,
                    sourceLabel: brandAssignmentQuery.data.sourceLabel,
                    versionName:
                      brandAssignmentQuery.data.effective?.versionName ||
                      brandAssignmentQuery.data.versionName,
                    mediaUrl: brandAssignmentQuery.data.effective?.mediaUrl || null,
                  }
                : null
            }
            error={screensaverError}
          />
          {industryField}
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={form.status === "Inactive" ? "Inactive" : "active"}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value }))
              }
            >
              <option value="active">Active</option>
              <option value="Inactive">Inactive</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModals}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={busy || !form.name.trim()}
            onClick={handleUpdate}
          >
            {busy ? "Saving…" : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!archivingBrand}
        onHide={closeArchiveModal}
        data-testid="brand-archive-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Archive &quot;{archivingBrand?.name}&quot;?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {archiveError && <Alert variant="danger">{archiveError}</Alert>}
          {archivePreflightLoading && (
            <div className="text-center py-3" data-testid="brand-archive-preflight-loading">
              <Spinner animation="border" size="sm" className="me-2" />
              Calculating platform-wide Archive impact…
            </div>
          )}
          {!archivePreflightLoading && archivePreflight && (
            <Alert
              variant="warning"
              className={styles.archiveImpactAlert}
              data-testid="brand-archive-impact"
            >
              <div className={styles.archiveWarningTitle}>
                {archivePreflight.warningTitle ||
                  "WARNING — PLATFORM-WIDE ACTION"}
              </div>
              <p className="mb-2">
                {archivePreflight.warningLead ||
                  (archivePreflight.assignedToClients
                    ? `This Brand is currently assigned to ${archivePreflight.affectedClientCount} Client Master Account(s).`
                    : "This Brand is not currently assigned to any Clients.")}
              </p>
              <p className="mb-2 fw-semibold">
                Archiving &quot;{archivingBrand?.name}&quot; will deactivate this
                Brand across KrossKast — not remove it from a single Client.
              </p>
              <ul className="mb-2 ps-3">
                {(archivePreflight.effects || []).map((effect) => (
                  <li key={effect}>{effect}</li>
                ))}
              </ul>
              {archivePreflight.assignedToClients ? (
                <div className="mb-0">
                  <Button
                    variant="link"
                    className="p-0 align-baseline"
                    onClick={() => setShowAffectedClients((open) => !open)}
                    aria-expanded={showAffectedClients}
                    data-testid="brand-archive-view-clients"
                  >
                    {showAffectedClients
                      ? "Hide affected Clients"
                      : "View affected Clients"}{" "}
                    ({archivePreflight.affectedClientCount})
                  </Button>
                  {showAffectedClients && (
                    <ul
                      className={`mb-0 mt-2 ps-3 ${styles.archiveClientList}`}
                      data-testid="brand-archive-affected-clients"
                    >
                      {(archivePreflight.affectedClients || []).map((client) => (
                        <li key={client.masterAccountId}>
                          {formatArchiveClientLabel(client)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <p className="mb-0 text-muted" data-testid="brand-archive-zero-clients">
                  {archivePreflight.zeroMembershipMessage ||
                    "This Brand is not currently assigned to any Clients."}
                </p>
              )}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>
              Type the Brand name to confirm ({archivingBrand?.name})
            </Form.Label>
            <Form.Control
              value={archiveNameConfirmation}
              onChange={(e) => setArchiveNameConfirmation(e.target.value)}
              autoComplete="off"
              disabled={
                archivePreflightLoading ||
                !archivePreflight ||
                archivePreflight.eligible !== true
              }
              data-testid="brand-archive-name-confirmation"
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>
              Reason (min {BRAND_LIFECYCLE_MIN_REASON_LENGTH} characters)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Why is this Brand being archived?"
              disabled={
                archivePreflightLoading ||
                !archivePreflight ||
                archivePreflight.eligible !== true
              }
              data-testid="brand-archive-reason"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeArchiveModal}
            disabled={archiving}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!archiveReady || archiving}
            onClick={() => void handleArchive()}
            data-testid="brand-archive-submit"
          >
            {archiving ? "Archiving…" : "Archive Brand platform-wide"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!restoringBrand}
        onHide={closeRestoreModal}
        size="lg"
        data-testid="brand-restore-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Restore &quot;{restoringBrand?.name}&quot;?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {restoreError && <Alert variant="danger">{restoreError}</Alert>}
          <p className="text-muted">
            Select which previously revoked memberships to re-grant. Uncheck any
            Master Account that should not regain access. An empty selection
            restores the Brand without re-granting memberships.
          </p>
          {restoreEligibilityLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" /> Loading eligible
              memberships…
            </div>
          ) : restoreEligibilityError ? (
            <Alert variant="danger">
              {extractApiErrorMessage(
                restoreEligibilityErr,
                "Failed to load eligible memberships",
              )}
            </Alert>
          ) : eligibleMemberships.length === 0 ? (
            <Alert variant="info" className="mb-3">
              No memberships were revoked by Brand Archive for this Brand. The
              Brand can still be restored without re-granting access.
            </Alert>
          ) : (
            <div className={styles.membershipList}>
              {eligibleMemberships.map((m) => {
                const checked = selectedMembershipIds.includes(m.membershipId);
                return (
                  <Form.Check
                    key={m.membershipId}
                    type="checkbox"
                    id={`restore-m-${m.membershipId}`}
                    className={styles.membershipCheck}
                    checked={checked}
                    onChange={(e) =>
                      setSelectedMembershipIds((prev) =>
                        toggleMembershipSelection(
                          prev,
                          m.membershipId,
                          e.target.checked,
                        ),
                      )
                    }
                    label={
                      <span title={`Master Account ID: ${m.masterAccountId}`}>
                        {formatRestoreMembershipLabel(m)}
                        {m.companyName ? (
                          <>
                            {" "}
                            <code className="text-muted small">
                              {m.masterAccountId}
                            </code>
                          </>
                        ) : null}
                      </span>
                    }
                  />
                );
              })}
            </div>
          )}
          <Form.Group className="mb-3 mt-3">
            <Form.Label>Notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={restoreNotes}
              onChange={(e) => setRestoreNotes(e.target.value)}
            />
          </Form.Group>
          <Form.Check
            type="checkbox"
            id="brand-restore-confirm"
            checked={restoreConfirmed}
            onChange={(e) => setRestoreConfirmed(e.target.checked)}
            label="I confirm restoring this Brand with the membership selection above."
            data-testid="brand-restore-confirm"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeRestoreModal}
            disabled={restoring}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={
              !restoreConfirmed ||
              restoring ||
              restoreEligibilityLoading ||
              restoreEligibilityError
            }
            onClick={() => void handleRestore()}
            data-testid="brand-restore-submit"
          >
            {restoring ? "Restoring…" : "Restore Brand"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!pdBrand}
        onHide={closePdModal}
        data-testid="brand-permanent-delete-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Permanently delete &quot;{pdBrand?.name}&quot;?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pdPhase === "preflight" ? (
            <div className="text-muted mb-3">Loading dependency summary…</div>
          ) : null}
          {pdError && <Alert variant="danger">{pdError}</Alert>}
          {pdPreflight ? (
            <div className="mb-3">
              <strong>Will remove</strong>
              <ul>
                {(pdPreflight.counts || [])
                  .filter((c) => c.value != null)
                  .map((c) => (
                    <li key={c.label}>
                      {c.label}: {c.value}
                    </li>
                  ))}
                {(pdPreflight.willRemove || []).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <strong>Will retain forever</strong>
              <ul>
                {(pdPreflight.willRetain || []).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              {!pdPreflight.eligible ? (
                <Alert variant="warning" className="mb-0">
                  Deletion blocked:{" "}
                  {(pdPreflight.blockers || [])
                    .map((b) => b.message)
                    .join(" ") || "Not eligible for permanent delete."}
                </Alert>
              ) : null}
            </div>
          ) : null}

          {pdPreflight?.eligible ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>
                  Type the Brand name to confirm ({pdBrand?.name})
                </Form.Label>
                <Form.Control
                  value={pdNameConfirmation}
                  onChange={(e) => setPdNameConfirmation(e.target.value)}
                  autoComplete="off"
                  disabled={deleting || pdPhase === "running"}
                  data-testid="brand-pd-name-confirmation"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  Type {BRAND_PERMANENT_DELETE_CONFIRM_TOKEN} to confirm
                </Form.Label>
                <Form.Control
                  value={pdConfirmTyped}
                  onChange={(e) => setPdConfirmTyped(e.target.value)}
                  autoComplete="off"
                  disabled={deleting || pdPhase === "running"}
                  data-testid="brand-pd-confirm-token"
                />
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>
                  Reason (min {BRAND_LIFECYCLE_MIN_REASON_LENGTH} characters)
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={pdReason}
                  onChange={(e) => setPdReason(e.target.value)}
                  disabled={deleting || pdPhase === "running"}
                  placeholder="Why is this Brand being permanently deleted?"
                  data-testid="brand-pd-reason"
                />
              </Form.Group>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closePdModal}
            disabled={deleting || pdPhase === "running"}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={
              !pdReady ||
              deleting ||
              pdPhase === "preflight" ||
              pdPhase === "running"
            }
            onClick={() => void handlePermanentDelete()}
            data-testid="brand-pd-submit"
          >
            {pdPhase === "running" || deleting
              ? "Deleting…"
              : "Permanently Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Brands;
